'use client';

import { FinancialIndependenceValues } from './types';
import { FinancialIndependenceTranslations } from './types';

interface CalculatorInputsProps {
  values: FinancialIndependenceValues;
  onValuesChange: (field: keyof FinancialIndependenceValues, value: number | string) => void;
  translations: FinancialIndependenceTranslations;
}

export default function CalculatorInputs({ values, onValuesChange, translations }: CalculatorInputsProps) {
  const handleSliderChange = (field: keyof FinancialIndependenceValues, value: number) => {
    onValuesChange(field, value);
  };

  const handleInputChange = (field: keyof FinancialIndependenceValues, value: string) => {
    if (field === 'currency') {
      onValuesChange(field, value);
      return;
    }
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue >= 0) {
      onValuesChange(field, numValue);
    }
  };

  return (
    <div className="space-y-8">
      <h3 className="text-sm font-semibold text-gray-800 mb-1">{translations.sectionFutureIncome}</h3>
      {/* Monthly Expenses */}
      <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <label htmlFor="monthly-expenses-input" className="text-md text-gray-700 font-medium">{translations.monthlyExpenses}</label>
          </div>
          <div className="flex items-center gap-2">
            <input
              id="monthly-expenses-input"
              type="text"
              value={values.monthlyExpenses.toLocaleString('sv-SE')}
              onChange={(e) => handleInputChange('monthlyExpenses', e.target.value.replace(/\s/g, ''))}
              className="w-24 text-right border-b-2 border-gray-300 focus:border-blue-500 focus:outline-none focus:ring-0 px-1 py-1 text-gray-800 font-semibold bg-transparent"
              aria-label={translations.monthlyExpenses}
            />
            <span className="text-gray-800 font-semibold">{values.currency}</span>
          </div>
        </div>
        <div className="relative">
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-green-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${Math.min((values.monthlyExpenses / 100000) * 100, 100)}%` }}
            ></div>
          </div>
          <input
            id="monthly-expenses-slider"
            type="range"
            min="1000"
            max="100000"
            step="1000"
            value={values.monthlyExpenses}
            onChange={(e) => handleSliderChange('monthlyExpenses', parseInt(e.target.value))}
            className="absolute inset-0 w-full h-2 cursor-pointer slider"
            aria-label={`${translations.monthlyExpenses} slider`}
          />
        </div>
        <div className="flex justify-between text-xs text-gray-500">
          <span>1 000 {values.currency}</span>
          <span>100 000 {values.currency}</span>
        </div>
      </div>

      {/* Dividend Yield */}
      <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4 mt-4">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <label htmlFor="dividend-yield-input" className="text-md text-gray-700 font-medium">{translations.dividendYield}</label>
          </div>
          <div className="flex items-center gap-2">
            <input
              id="dividend-yield-input"
              type="number"
              value={values.dividendYield}
              onChange={(e) => handleInputChange('dividendYield', e.target.value)}
              className="w-20 text-right border-b-2 border-gray-300 focus:border-blue-500 focus:outline-none focus:ring-0 px-1 py-1 text-gray-800 font-semibold bg-transparent"
              min="0"
              max="20"
              step="0.1"
              aria-label={translations.dividendYield}
            />
            <span className="text-gray-800 font-semibold">%</span>
          </div>
        </div>
        <div className="relative">
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-green-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(values.dividendYield / 20) * 100}%` }}
            ></div>
          </div>
          <input
            id="dividend-yield-slider"
            type="range"
            min="0"
            max="20"
            step="0.1"
            value={values.dividendYield}
            onChange={(e) => handleSliderChange('dividendYield', parseFloat(e.target.value))}
            className="absolute inset-0 w-full h-2 cursor-pointer slider"
            aria-label={`${translations.dividendYield} slider`}
          />
        </div>
        <div className="flex justify-between text-xs text-gray-500">
          <span>0%</span>
          <span>20%</span>
        </div>
        <p className="text-xs text-gray-500">{translations.dividendYieldNote}</p>
      </div>

      <h3 className="text-sm font-semibold text-gray-800 mb-1">{translations.sectionSavings}</h3>
      {/* Current Savings */}
      <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <label htmlFor="current-savings-input" className="text-md text-gray-700 font-medium">{translations.currentSavings}</label>
          </div>
          <div className="flex items-center gap-2">
            <input
              id="current-savings-input"
              type="text"
              value={values.currentSavings.toLocaleString('sv-SE')}
              onChange={(e) => handleInputChange('currentSavings', e.target.value.replace(/\s/g, ''))}
              className="w-24 text-right border-b-2 border-gray-300 focus:border-blue-500 focus:outline-none focus:ring-0 px-1 py-1 text-gray-800 font-semibold bg-transparent"
              aria-label={translations.currentSavings}
            />
            <span className="text-gray-800 font-semibold">{values.currency}</span>
          </div>
        </div>
        <div className="relative">
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-green-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${Math.min((values.currentSavings / 10000000) * 100, 100)}%` }}
            ></div>
          </div>
          <input
            id="current-savings-slider"
            type="range"
            min="0"
            max="10000000"
            step="10000"
            value={values.currentSavings}
            onChange={(e) => handleSliderChange('currentSavings', parseInt(e.target.value))}
            className="absolute inset-0 w-full h-2 cursor-pointer slider"
            aria-label={`${translations.currentSavings} slider`}
          />
        </div>
        <div className="flex justify-between text-xs text-gray-500">
          <span>0 {values.currency}</span>
          <span>10 000 000 {values.currency}</span>
        </div>
      </div>

      {/* Monthly Contribution */}
      <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4 mt-4">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <label htmlFor="monthly-contribution-input" className="text-md text-gray-700 font-medium">{translations.monthlyContribution}</label>
          </div>
          <div className="flex items-center gap-2">
            <input
              id="monthly-contribution-input"
              type="text"
              value={values.monthlyContribution.toLocaleString('sv-SE')}
              onChange={(e) => handleInputChange('monthlyContribution', e.target.value.replace(/\s/g, ''))}
              className="w-24 text-right border-b-2 border-gray-300 focus:border-blue-500 focus:outline-none focus:ring-0 px-1 py-1 text-gray-800 font-semibold bg-transparent"
              aria-label={translations.monthlyContribution}
            />
            <span className="text-gray-800 font-semibold">{values.currency}</span>
          </div>
        </div>
        <div className="relative">
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-green-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${Math.min((values.monthlyContribution / 50000) * 100, 100)}%` }}
            ></div>
          </div>
          <input
            id="monthly-contribution-slider"
            type="range"
            min="0"
            max="50000"
            step="500"
            value={values.monthlyContribution}
            onChange={(e) => handleSliderChange('monthlyContribution', parseInt(e.target.value))}
            className="absolute inset-0 w-full h-2 cursor-pointer slider"
            aria-label={`${translations.monthlyContribution} slider`}
          />
        </div>
        <div className="flex justify-between text-xs text-gray-500">
          <span>0 {values.currency}</span>
          <span>50 000 {values.currency}</span>
        </div>
      </div>

      {/* Investment Return */}
      <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4 mt-4">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <label htmlFor="investment-return-input" className="text-md text-gray-700 font-medium">{translations.investmentReturn}</label>
          </div>
          <div className="flex items-center gap-2">
            <input
              id="investment-return-input"
              type="number"
              value={values.investmentReturn}
              onChange={(e) => handleInputChange('investmentReturn', e.target.value)}
              className="w-20 text-right border-b-2 border-gray-300 focus:border-blue-500 focus:outline-none focus:ring-0 px-1 py-1 text-gray-800 font-semibold bg-transparent"
              min="0"
              max="20"
              step="0.1"
              aria-label={translations.investmentReturn}
            />
            <span className="text-gray-800 font-semibold">%</span>
          </div>
        </div>
        <div className="relative">
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-green-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(values.investmentReturn / 20) * 100}%` }}
            ></div>
          </div>
          <input
            id="investment-return-slider"
            type="range"
            min="0"
            max="20"
            step="0.1"
            value={values.investmentReturn}
            onChange={(e) => handleSliderChange('investmentReturn', parseFloat(e.target.value))}
            className="absolute inset-0 w-full h-2 cursor-pointer slider"
            aria-label={`${translations.investmentReturn} slider`}
          />
        </div>
        <div className="flex justify-between text-xs text-gray-500">
          <span>0%</span>
          <span>20%</span>
        </div>
      </div>

      <style jsx>{`
        .slider::-webkit-slider-thumb {
          appearance: none;
          height: 20px;
          width: 20px;
          border-radius: 50%;
          background: #10b981;
          cursor: pointer;
          border: 2px solid #ffffff;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        }
        
        .slider::-moz-range-thumb {
          height: 20px;
          width: 20px;
          border-radius: 50%;
          background: #10b981;
          cursor: pointer;
          border: 2px solid #ffffff;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        }
      `}</style>
    </div>
  );
}
